/* global window, document, FileReader, Image */
import { createGuid } from 'app/types/guid';
import { createMockableFunction } from 'app/utils/mock';
import { isUnknownObject } from 'app/utils/object';
import debounce from 'lodash/debounce';
import merge from 'lodash/merge';
import React from 'react';

/**
 * This is intended to be used instead of using `window` directly. When using
 * `window` directly, the type of `window` is never `undefined`, even though
 * many parts of the codebase may be run in a Node environment (such as when
 * generating sitemaps or other scripts). Because `getWindow()` returns `Window
 * | undefined`, it forces the code to handle the possibility of running in a
 * non-DOM environment.
 *
 * When the entire codebase is updated to use this instead of accessing `window`
 * directly, we can enable this linting rule to prevent people from accidentally
 * using `window` in the future:
 *
 * ```
 * 'no-restricted-globals': [
 *   'error',
 *   {
 *     name: 'window',
 *     message: 'Use getWindow() or getWindowOrThrow() instead.',
 *   },
 * ],
 * ```
 */
export const getWindow = (): Window | undefined => (
  typeof window !== 'undefined' ? window : undefined
);

/**
 * In cases where there is no reasonable fallback behavior and the code should
 * never be run in a non-DOM environment anyway, this can be used instead of
 * `getWindow()`.
 */
export const getWindowOrThrow: () => Window = () => {
  if (typeof window !== 'undefined') {
    return window;
  }

  throw new Error('Global window object not found.');
};

export const dispatchEvent: Window['dispatchEvent'] = (...args) =>
  window.dispatchEvent(...args);

export const nowSeconds = createMockableFunction(() =>
  Math.floor(Date.now() / 1000),
);

/**
 * Returns true if the bundle is executing in the context of the `serverRender` script.
 */
export const isServerRendering = () =>
  typeof window !== 'undefined' && isUnknownObject(window) && window.isServerRendering === true;

// Note: There used to be getHostname()/getPathname()/etc. functions here for accessing
// window.location. However, when server rendering window.location will not reflect the URL of the
// page that is being rendered. Instead, you should use `getGlobalHistory().location` from
// `app/services/history` to get this information.

// TODO: Merge this functionality into global history object.
export const getHistoryState = () =>
  (isUnknownObject(window.history.state) && window.history.state) || ({});

// You should probably rather use mergeIntoHistoryState
const setHistoryState = (state: unknown) =>
  window.history.replaceState(state, '', window.location.toString());

export const mergeIntoHistoryState = (() => {
  let diffs: unknown[] = [];

  const mergeAll = debounce(() => {
    const state = merge({}, getHistoryState(), ...diffs);
    diffs = [];
    setHistoryState(state);
  }, 300);

  return (diff: unknown) => {
    diffs.push(diff);
    mergeAll();
  };
})();

const mergeHistoryState = (diff: unknown) => {
  const state = merge({}, getHistoryState(), diff);
  setHistoryState(state);
};

const getLocationKey = () => getHistoryState().locationKey as string | undefined;

export const getOrCreateLocationKey = () => {
  let locationKey = getLocationKey();
  if (!locationKey) {
    locationKey = createGuid();
    mergeHistoryState({ locationKey });
  }
  return locationKey;
};

export const getElementById = (query: string) =>
  window.document.getElementById(query);

export const scrollWindow = (x: number, y: number) => {
  // temporary hack to prevent trying to scroll during ssr
  if (!isServerRendering()) {
    window.scrollTo(x, y);
  }
};

export const readFileDataURL = (file: Blob): Promise<string> => new Promise((resolve, reject) => {
  try {
    const reader = new FileReader();

    // the result attribute contains the data as a data: URL representing the
    // file's data as a base64 encoded string.
    // https://developer.mozilla.org/en-US/docs/Web/API/FileReader/readAsDataURL
    reader.addEventListener('load', () => resolve(reader.result as string));
    reader.addEventListener('error', reject);

    reader.readAsDataURL(file);
  } catch (error) {
    reject(error);
  }
});

export const readBlob = (blob: Blob): Promise<ArrayBuffer> => new Promise((resolve, reject) => {
  try {
    const reader = new FileReader();

    // the result attribute contains an ArrayBuffer representing the file's data.
    // https://developer.mozilla.org/en-US/docs/Web/API/FileReader/readAsArrayBuffer
    reader.addEventListener('load', () => resolve(reader.result as ArrayBuffer));
    reader.addEventListener('error', reject);

    reader.readAsArrayBuffer(blob);
  } catch (error) {
    reject(error);
  }
});

// it'd be nice to use node.remove() but IE11 doesn't support it.
export const removeDomElement = (node: Element) => node.parentNode?.removeChild(node);

// This is potentially unsafe, since it can throw errors when trying to access it
// or using certain methods depending on the state of the browser and the state of the host OS.
// Use redux localStorage for a safer alternative, which wraps native localStorage
export const getDangerousNativeLocalStorage = () => window.localStorage;

// This is potentially unsafe, since it can throw errors when trying to access it
// or using certain methods depending on the state of the browser and the state of the host OS.
// Use redux sessionStorage for a safer alternative, which wraps native sessionStorage
export const getDangerousNativeSessionStorage = () => window.sessionStorage;

export const getUserAgent = createMockableFunction(() => window.navigator.userAgent);

export const eventHasModifierKeys = (
  event: React.KeyboardEvent | React.MouseEvent,
  modifiers = ['Control', 'Alt', 'Shift', 'Meta', 'OS', 'Win'],
) => (
  typeof event.getModifierState === 'function' ?
    modifiers.some(key => event.getModifierState(key)) :
    false // This case is mainly for fake events dispatched in tests
);

export const injectScript = (url: string) => {
  const script = document.createElement('script');
  script.async = true;
  script.src = url;
  document.body.appendChild(script);
  return script;
};

export const waitForScriptToLoad = (scriptNode: HTMLScriptElement) =>
  new Promise((resolve, reject) => {
    // eslint-disable-next-line no-param-reassign
    scriptNode.onload = () => resolve(scriptNode);
    // eslint-disable-next-line no-param-reassign
    scriptNode.onerror = reject;
  });


/**
* Whether or not the page a currently hidden. This is based on whether or not the tab is the
* the foreground or not.
*/
export const isPageHidden = createMockableFunction(() => document.visibilityState !== 'visible');

/**
 * Listens to the global `storage` event, but only for events whose `storageArea` matches
 * `targetStorageArea`.
 */
const makeStorageListener = (targetStorageArea: Storage) =>
  (listener: (e: StorageEvent) => void) => {
    const wrappedListener = (event: StorageEvent) => {
      if (event.storageArea === targetStorageArea) {
        listener(event);
      }
    };

    getWindowOrThrow().addEventListener('storage', wrappedListener, false);

    return () => window.removeEventListener('storage', wrappedListener, false);
  };

export const copySelectedToClipboard = () => {
  document.execCommand('copy');
};

export const getImage = (src: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const image = new Image();
  image.addEventListener('load', () => resolve(image));
  image.addEventListener('error', reject);
  image.src = src;
});

export const confirm = (message: string) => window.confirm(message); // eslint-disable-line no-alert

export const addLocalStorageListener = createMockableFunction(getWindow()
  ? makeStorageListener(window.localStorage)
  : () => () => {});

export const addSessionStorageListener = getWindow()
  ? makeStorageListener(window.sessionStorage)
  : () => () => {};

export const isStorageClearEvent = (storageEvent: Partial<StorageEvent>) => (
  !storageEvent.key && !storageEvent.oldValue && !storageEvent.newValue
);

export const getWindowScrollPosition = () => ({
  x: window.scrollX || window.document.documentElement.scrollLeft,
  y: window.scrollY || window.document.documentElement.scrollTop,
});

export const getInnerWindowSize = () => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

export const getWindowScrollSize = () => ({
  scrollWidth: window.document.documentElement.scrollWidth,
  scrollHeight: window.document.documentElement.scrollHeight,
});

/**
 * Posts a message to the frame renderer.
 */
export const postParentMessage = (message: unknown, target: string) => {
  if (typeof window !== 'undefined' && 'parent' in window && 'postMessage' in window.parent) {
    window.parent.postMessage(message, target);
  } else {
    return;
  }
};

export const createElement = <K extends keyof HTMLElementTagNameMap>(name: K) => document.createElement<K>(name);

export const getMetaTag = (name: string) => {
  const metaElement = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  return (metaElement && metaElement.content) || undefined;
};
